import React, {useMemo, useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {FlexColumn, FlexRow, Label, Modal, Text} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import CreateProjectBillToMutation from '../../../../mutations/invoicing/CreateProjectBillToMutation';
import Util from '../../../shared/util/util';
import type {AddProjectBillToModal_viewer$data as AddProjectBillToModal_viewer} from './__generated__/AddProjectBillToModal_viewer.graphql';
import ConditionalDropdown from '../../../../components/dropdowns/conditional-dropdown/ConditionalDropdown';
import InvoicingIntegrationIcon from '../InvoicingIntegrationIcon';

type AddProjectBillToModalProps = {
	projectId: string;
	closeModal: () => void;
	viewer: AddProjectBillToModal_viewer;
};

type BillFromItem = {id: string; name: string; integration: string};
type BillToItem = {id: string; name: string; billFromId: string};
type ClientBillToItem = {id: string; clientId: string; billTo: BillToItem; billFrom: BillFromItem};

const getKeyFromIds = (billFromId: string, billToId: string) => {
	return billFromId + '-' + billToId;
};

const AddProjectBillToModal = ({projectId, closeModal, viewer}: AddProjectBillToModalProps): JSX.Element => {
	const {formatMessage} = useIntl();

	const clientBillTos = useMemo<ClientBillToItem[]>(() => {
		const projectBillToKeys = new Set();
		for (const projectBillTo of viewer?.project?.projectBillTos?.edges || []) {
			projectBillToKeys.add(
				getKeyFromIds(projectBillTo?.node?.billFrom?.id || '', projectBillTo?.node?.billTo?.id || '')
			);
		}
		return (viewer?.project?.client?.clientBillTos?.edges
			?.map(clientBillTo => clientBillTo?.node)
			.filter(
				clientBillTo =>
					!projectBillToKeys.has(getKeyFromIds(clientBillTo?.billFrom?.id || '', clientBillTo?.billTo?.id || ''))
			) || []) as ClientBillToItem[];
	}, [viewer]);

	const billFromOptions = useMemo(() => {
		const billFromKeys = new Set();
		for (const clientBillTo of clientBillTos) {
			billFromKeys.add(clientBillTo?.billFrom?.id);
		}

		const billFroms = (viewer?.project?.client?.clientBillTos?.edges
			?.map(clientBillTo => clientBillTo?.node?.billFrom)
			.filter(billFrom => billFrom && billFromKeys.has(billFrom.id)) || []) as BillFromItem[];
		const billFromOptions: BillFromItem[] = [];
		const billFromIds = new Set();
		for (const billFrom of billFroms) {
			if (!billFrom) continue;
			if (!billFromIds.has(billFrom?.id)) {
				billFromOptions.push(billFrom);
				billFromIds.add(billFrom.id);
			}
		}
		return billFromOptions;
	}, [viewer, clientBillTos]);

	const [selectedBillFrom, setSelectedBillFrom] = useState<BillFromItem>();
	const [selectedClientBillTo, setSelectedClientBillTo] = useState<ClientBillToItem>();
	const clientBillToOptions = useMemo<ClientBillToItem[]>(
		() =>
			selectedBillFrom ? clientBillTos.filter(clientBillTo => clientBillTo?.billFrom?.id === selectedBillFrom?.id) : [],
		[selectedBillFrom, clientBillTos]
	);

	const addProjectBillTo = () => {
		const onSuccess = () => {
			closeModal();
		};
		if (selectedClientBillTo) {
			Util.CommitMutation(
				CreateProjectBillToMutation,
				{
					billToId: selectedClientBillTo.billTo.id,
					clientBillToId: selectedClientBillTo.id,
					clientId: selectedClientBillTo.clientId,
					projectId: projectId,
					billFromId: selectedClientBillTo.billFrom.id,
				},
				onSuccess
			);
		}
	};

	const BillFromItem = ({billFrom}) => (
		<FlexRow gap={'s'}>
			<InvoicingIntegrationIcon integration={billFrom?.integration} />
			<Text>{billFrom?.name}</Text>
		</FlexRow>
	);

	return (
		<Modal onCloseModal={closeModal}>
			<Modal.Title>{formatMessage({id: 'project_settings.add_billing_details'})}</Modal.Title>
			<Modal.Content>
				<FlexColumn gap="xl">
					<FlexColumn gap="xs">
						<Label>{formatMessage({id: 'common.bill_from'})}</Label>
						<ConditionalDropdown
							options={billFromOptions}
							renderOption={(billFrom: BillFromItem) => (
								<FlexRow>
									<BillFromItem billFrom={billFrom} />
								</FlexRow>
							)}
							onSelect={setSelectedBillFrom}
						>
							<FlexRow>
								<BillFromItem billFrom={selectedBillFrom} />
							</FlexRow>
						</ConditionalDropdown>
					</FlexColumn>
					<FlexColumn gap="xs">
						<Label>{formatMessage({id: 'common.bill_to'})}</Label>
						<ConditionalDropdown
							options={clientBillToOptions}
							renderOption={(item: ClientBillToItem) => (
								<FlexRow>
									<Text>{item.billTo.name}</Text>
								</FlexRow>
							)}
							onSelect={setSelectedClientBillTo}
						>
							<FlexRow>
								<Text>{selectedClientBillTo?.billTo?.name || ''}</Text>
							</FlexRow>
						</ConditionalDropdown>
					</FlexColumn>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={addProjectBillTo}>
					{formatMessage({id: 'common.confirm'})}
				</Modal.PrimaryFooterButton>

				<Modal.SecondaryFooterButton onPress={closeModal}>
					{formatMessage({id: 'common.close'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export const AddProjectBillToModalQuery = graphql`
	query AddProjectBillToModalQuery($projectId: ID!) {
		viewer {
			actualPersonId
			component(name: "AddProjectBillToModal")
			...AddProjectBillToModal_viewer @arguments(id: $projectId)
		}
	}
`;

export default createFragmentContainer(AddProjectBillToModal, {
	viewer: graphql`
		fragment AddProjectBillToModal_viewer on Viewer @argumentDefinitions(id: {type: "ID!"}) {
			id
			project(internalId: $id) {
				id
				projectBillTos(first: 10000) {
					edges {
						node {
							id
							billTo {
								id
							}
							billFrom {
								id
							}
						}
					}
				}
				client {
					clientBillTos(first: 10000) {
						edges {
							node {
								id
								clientId
								billTo {
									id
									name
									billFromId
								}
								billFrom {
									id
									name
									integration
								}
							}
						}
					}
				}
			}
			company {
				billFroms {
					id
					name
				}
			}
		}
	`,
});
